import { computed, ref, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { debounce } from 'lodash';
import { escapeSql } from '@rdrive/helper-sql';
import { useControllerCategoryChart } from '@/charts/configs/useControllerCategoryChart';
import { useControllerDonutChart } from '@/charts/configs/useControllerDonutChart';
import { useI18n } from 'vue-i18n';
import { useAppContext } from '@/composables/useAppContext';
import { useItemsGrid } from '@/composables/useItemsGrid';
import { getCurrentUserTeamIds, toSqlIdList } from '@/utils/userTeams';

export default () => {
  const store = useStore();
  const { t } = useI18n();
  const dataLoaded = computed(() => store.getters.getDataLoaded);

  const filterContext = computed(() => store.state.filterItemsContext);
  const userId = computed(() => store.getters.getUserId);
  const chartFontColor = computed(() => store.getters.chartFontColor);
  const conn = store.getters.getDBConnection;

  const controllerCategoryChart = useControllerCategoryChart({
    chartName: 'controllerCategoryStackedBarChart',
    filterContext,
    conn
  });

  const controllerDonutChart = useControllerDonutChart({
    chartName: 'controllerTeamDonutChart',
    filterContext,
    conn
  });

  const app = useAppContext();
  const grid = useItemsGrid({ ...app, filterContext: app.filterItemsContext, t });

  const totalItems = ref(0);
  const totalSubItems = ref(0);
  const totalControlledByYourTeams = ref(0);
  const totalCreatedByYourTeams = ref(0);
  const totalCreatedByYou = ref(0);

  const filterByControlledByMyTeams = ref(false);
  const filterByCreatedByMyTeams = ref(false);
  const filterByCreatedByYou = ref(false);

  const computeCardValues = async () => {
    if (!dataLoaded.value || !isMounted.value) return;

    const myTeamIds = await getCurrentUserTeamIds(conn, userId.value);
    const teamIdsList = toSqlIdList(myTeamIds);

    // Build a combined query to get all counts at once
    const combinedQuery = `
    SELECT
        COUNT(DISTINCT id) AS TotalItems,
        COUNT(*) AS TotalSubItems,
        COUNT(CASE WHEN controlledByTeamId IN (${teamIdsList}) THEN id END) AS ControlledByYourTeams,
        COUNT(CASE WHEN createdByTeamId IN (${teamIdsList}) THEN id END) AS CreatedByYourTeams,
        COUNT(CASE WHEN createdByUserId = '${userId.value}' THEN id END) AS CreatedByYou
    FROM filteredItems
  `;

    try {
      const result = await conn.query(combinedQuery);
      const row = result.toArray()[0];
    
      if (!row) {
        totalItems.value = 0;
        totalSubItems.value = 0;
        totalControlledByYourTeams.value = 0;
        totalCreatedByYourTeams.value = 0;
        totalCreatedByYou.value = 0;
        return;
      }

      const jsonRow = row.toJSON();
    
      totalItems.value = Number(jsonRow.TotalItems);
      totalSubItems.value = Number(jsonRow.TotalSubItems);
      totalControlledByYourTeams.value = Number(jsonRow.ControlledByYourTeams);
      totalCreatedByYourTeams.value = Number(jsonRow.CreatedByYourTeams);
      totalCreatedByYou.value = Number(jsonRow.CreatedByYou);
    
    } catch (error) {
      console.error("Error executing combined query:", error);
      totalItems.value = 0;
      totalSubItems.value = 0;
      totalControlledByYourTeams.value = 0;
      totalCreatedByYourTeams.value = 0;
      totalCreatedByYou.value = 0;
    }
  };

  const handleFilterChange = ({ id, selected }) => {
    if (id === 'totalControlledByMyTeams') {
      filterByControlledByYourTeams(selected);
    } else if (id === 'totalCreatedByYourTeams') {
      filterByCreatedByYourTeams(selected);
    } else if (id === 'totalCreatedByYou') {
      filterByCreatedByMe(selected);
    }
  };

  const applyTeamCardFilter = async (visualId, teamColumn, selected) => {
    try {
      if (!selected) {
        await filterContext.value.removeFilter(visualId);
        return;
      }
      const myTeamIds = await getCurrentUserTeamIds(conn, userId.value);
      const teamIdsList = toSqlIdList(myTeamIds);
      await filterContext.value.updateFilterWithQuery(visualId, `
      SELECT SubItemId FROM items WHERE ${teamColumn} IN (${teamIdsList})
    `);
    } catch (error) {
      console.error(`Error applying ${visualId} filter:`, error);
    }
  };

  const filterByControlledByYourTeams = (selected) =>
    applyTeamCardFilter('controlledByYourTeamCardFilterContext', 'ControlledByTeamId', selected);

  const filterByCreatedByYourTeams = (selected) =>
    applyTeamCardFilter('createdByYourTeamCardFilterContext', 'CreatedByTeamId', selected);

  const filterByCreatedByMe = async (selected) => {
    try {
      if (!selected) {
        await filterContext.value.removeFilter('createdByYouCardFilterContext');
        return;
      }
      await filterContext.value.updateFilterWithQuery('createdByYouCardFilterContext', `
      SELECT SubItemId FROM items WHERE CreatedByUserId = '${escapeSql(userId.value)}'
    `);
    } catch (error) {
      console.error("Error filtering by created by me:", error);
    }
  };

  const recomputeCounts = debounce(() => {
    computeCardValues();
  }, 100);

  const isMounted = ref(false);

  onMounted(async () => {
    await store.commit('resetFilters');
    isMounted.value = true;
    recomputeCounts();
  });

  watch(
    () => filterContext.value.filtersLastUpdated,
    () => {
      if (isMounted.value) {
        recomputeCounts();
      }
    },
    { deep: true }
  );

  return {
    dataLoaded,
    filterContext,
    chartFontColor,
    controllerCategoryChart,
    controllerDonutChart,
    grid,
    totalItems,
    totalSubItems,
    totalControlledByYourTeams,
    totalCreatedByYourTeams,
    totalCreatedByYou,
    filterByControlledByMyTeams,
    filterByCreatedByMyTeams,
    filterByCreatedByYou,
    handleFilterChange
  };
};
